import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, CardBody, FormFeedback, Input, Label } from "reactstrap";

import ProfessionalAvatar from "../../../Components/Common/ProfessionalAvatar";
import {
  PublicBookingAvailabilitySlot,
  PublicBookingCustomerPayload,
  PublicBookingProfessional,
  PublicBookingService,
} from "../../../services/publicBookingService";

interface SummaryStepProps {
  service: PublicBookingService;
  professional: PublicBookingProfessional;
  slot: PublicBookingAvailabilitySlot;
  submitting: boolean;
  submitError: string | null;
  onBack: () => void;
  onSubmit: (customer: PublicBookingCustomerPayload) => void;
}

const formatDateLabel = (datetime: string): string => {
  const [datePart] = datetime.split(" ");
  const [year, month, day] = datePart.split("-");

  return `${day}/${month}/${year}`;
};

const formatTimeLabel = (datetime: string): string => datetime.slice(11, 16);

const isValidEmail = (value: string): boolean => {
  if (!value.trim()) {
    return true;
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

const isValidPhone = (digits: string): boolean => digits.length >= 10 && digits.length <= 15;

const SummaryStep: React.FC<SummaryStepProps> = ({
  service,
  professional,
  slot,
  submitting,
  submitError,
  onBack,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState({ name: false, phone: false, email: false });

  const phoneDigits = useMemo(() => phoneDisplay.replace(/\D/g, ""), [phoneDisplay]);

  const nameError = touched.name && name.trim().length < 2
    ? t("public_booking.summary.errors.nameRequired")
    : null;
  const phoneError = touched.phone && !isValidPhone(phoneDigits)
    ? t("public_booking.summary.errors.phoneInvalid")
    : null;
  const emailError = touched.email && !isValidEmail(email)
    ? t("public_booking.summary.errors.emailInvalid")
    : null;

  const canSubmit = name.trim().length >= 2
    && isValidPhone(phoneDigits)
    && isValidEmail(email)
    && !submitting;

  const handleSubmit = (): void => {
    setTouched({ name: true, phone: true, email: true });

    if (!canSubmit) {
      return;
    }

    onSubmit({
      name: name.trim(),
      country_code: "55",
      phone_number: phoneDigits,
      email: email.trim() || undefined,
    });
  };

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
        <div>
          <p className="text-muted mb-1">{t("public_booking.summary.title")}</p>
          <h5 className="mb-0">{t("public_booking.summary.subtitle")}</h5>
        </div>
        <Button color="light" size="sm" onClick={onBack} disabled={submitting}>
          <i className="ri-arrow-left-line align-bottom me-1" />
          {t("public_booking.common.back")}
        </Button>
      </div>

      <Card className="border bg-light shadow-none mb-4">
        <CardBody>
          <p className="text-muted mb-2">{t("public_booking.summary.attendingLabel")}</p>
          <div className="d-flex align-items-center gap-3 mb-3">
            <ProfessionalAvatar
              name={professional.name}
              colorHex={professional.color_hex}
              photoUrl={professional.photo_url}
              size={56}
            />
            <div>
              <h5 className="fs-16 mb-1">{professional.name}</h5>
              <p className="text-muted mb-0">{service.name}</p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-3">
            <div>
              <small className="text-muted d-block">{t("public_booking.summary.date")}</small>
              <span className="fw-medium">{formatDateLabel(slot.start_datetime)}</span>
            </div>
            <div>
              <small className="text-muted d-block">{t("public_booking.summary.time")}</small>
              <span className="fw-medium">
                {formatTimeLabel(slot.start_datetime)}
                {" – "}
                {formatTimeLabel(slot.end_datetime)}
              </span>
            </div>
            <div>
              <small className="text-muted d-block">{t("public_booking.summary.price")}</small>
              <span className="fw-medium text-primary">{service.price_display}</span>
              {service.price_is_from ? (
                <small className="text-muted d-block mt-1">
                  {t("public_booking.summary.priceFromHint")}
                </small>
              ) : null}
            </div>
          </div>
        </CardBody>
      </Card>

      {submitError ? (
        <Alert color="danger" className="mb-3">
          {submitError}
        </Alert>
      ) : null}

      <div className="mb-3">
        <Label for="public-booking-name" className="form-label">
          {t("public_booking.summary.nameLabel")}
        </Label>
        <Input
          id="public-booking-name"
          value={name}
          invalid={Boolean(nameError)}
          disabled={submitting}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => setTouched((current) => ({ ...current, name: true }))}
        />
        {nameError ? <FormFeedback>{nameError}</FormFeedback> : null}
      </div>

      <div className="mb-3">
        <Label for="public-booking-phone" className="form-label">
          {t("public_booking.summary.phoneLabel")}
        </Label>
        <Input
          id="public-booking-phone"
          type="tel"
          inputMode="numeric"
          value={phoneDisplay}
          invalid={Boolean(phoneError)}
          disabled={submitting}
          placeholder="(11) 99999-9999"
          onChange={(event) => setPhoneDisplay(event.target.value)}
          onBlur={() => setTouched((current) => ({ ...current, phone: true }))}
        />
        {phoneError ? <FormFeedback>{phoneError}</FormFeedback> : null}
      </div>

      <div className="mb-4">
        <Label for="public-booking-email" className="form-label">
          {t("public_booking.summary.emailLabel")}
        </Label>
        <Input
          id="public-booking-email"
          type="email"
          value={email}
          invalid={Boolean(emailError)}
          disabled={submitting}
          onChange={(event) => setEmail(event.target.value)}
          onBlur={() => setTouched((current) => ({ ...current, email: true }))}
        />
        <small className="text-muted">{t("public_booking.summary.emailHint")}</small>
        {emailError ? <FormFeedback>{emailError}</FormFeedback> : null}
      </div>

      <div className="d-grid">
        <Button color="primary" size="lg" disabled={!canSubmit} onClick={handleSubmit}>
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              {t("public_booking.summary.confirming")}
            </>
          ) : (
            t("public_booking.summary.confirm")
          )}
        </Button>
      </div>
    </>
  );
};

export default SummaryStep;
